import Button from "@/components/Button/Button.component";
import { BUTTON_SUBTYPE_CLASS } from "@/components/Button/Button.model";
import Icon from "@/components/Icon/Icon.component";
import { ICON_TYPE } from "@/components/Icon/Icon.model";
import Image from "@/components/Image/Image.component";
import { RichText } from "@/components/RichText/RichText.component";
import { Typography } from "@/components/Typography/Typography.component";
import { ShowContactActionKind } from "@/context/PageContext/PageContext.model";
import { usePageContext } from "@/context/PageContext/PageContextProvider";
import {
  useContentfulInspectorMode,
  useContentfulLiveUpdates,
} from "@contentful/live-preview/react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import Error from "next/error";
import { useRef, useState } from "react";
import {
  ContactPropsModel,
  ContactTopic,
  ContactUsModel,
  FormValues,
} from "./Contact.model";
import { ContactForm } from "./ContactForm/ContactForm.component";

export default function Contact(props: ContactPropsModel) {
  const titleWrapper = useRef<HTMLDivElement>(null);
  const titleAll = useRef<HTMLDivElement>(null);
  const titleOne = useRef<HTMLDivElement>(null);
  const titleTwo = useRef<HTMLDivElement>(null);
  const contactWrapper = useRef<HTMLDivElement>(null);
  const contactAll = useRef<HTMLDivElement>(null);
  const contactOne = useRef<HTMLDivElement>(null);
  const contactTwo = useRef<HTMLDivElement>(null);

  const { dispatch } = usePageContext();

  let inspectorProps;
  let liveData = props;
  let contentfulProps;
  if (props?.sys?.id) {
    inspectorProps = useContentfulInspectorMode({
      entryId: props.sys.id,
    });
    contentfulProps = inspectorProps({ fieldId: "stepOneTitle" });
    liveData = useContentfulLiveUpdates(props);
  }

  const {
    stepOneTitle,
    stepTwoTitle,
    stepOneDescription,
    stepTwoDescription,
    intro,
    topicsCollection,
    formSubmittedTitle,
    formSubmittedDescription,
    formSubmittedMessage: { json },
    errorMessage: { json: error },
    setShowContact,
  } = liveData;

  const closeContactModule = () => {
    dispatch({
      type: ShowContactActionKind.SET_CONTACT_STATUS,
      payload: { showContact: false },
    });
    setShowContact(false);
  };

  const [topic, setTopic] = useState<ContactTopic>();
  const [step, setStep] = useState<number>();
  const [titleHeight, setTitleHeight] = useState<number>();
  const [contactHeight, setContactHeight] = useState<number>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);

  const handleContact = (text: string) => {
    const defaultTopic = topic || topicsCollection[0];

    return text
      .replace("#TOPIC#", defaultTopic?.topic.toLowerCase())
      .replace("#PERSON#", defaultTopic?.name)
      .replace("#ROLE#", defaultTopic?.role);
  };

  // sliding animations
  useGSAP(() => {
    const scroll = gsap.to(titleAll.current, {
      duration: 0.3,
      startAt: { y: 0 },
      y: () => -(titleOne.current?.clientHeight || 0),
      ease: "none",
      paused: true,
      runBackwards: step === 1,
    });

    const slide = gsap.to(contactAll.current, {
      duration: 0.3,
      startAt: { x: 0 },
      x: () => -(contactOne.current?.clientWidth || 0),
      ease: "none",
      paused: true,
      runBackwards: step === 1,
    });

    if (step) {
      scroll.play();
      slide.play();
    }

    if (step === 2) {
      setTitleHeight(titleTwo.current?.clientHeight);
      // set Contact Height to be the smaller number between div height and screen height to avoid overflow
      setContactHeight(
        contactTwo.current?.clientHeight
          ? Math.min(
              contactTwo.current.clientHeight,
              window.innerHeight - (titleTwo.current?.clientHeight || 0) - 124,
            )
          : undefined,
      );
    } else {
      setTitleHeight(titleOne.current?.clientHeight);
      setContactHeight(contactOne.current?.clientHeight);
    }
  }, [step]);

  const pushToDataLayer = (obj: ContactUsModel) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(obj);
  };

  const handleSubmit = async (data: FormData) => {
    setIsLoading(true);
    const obj: ContactUsModel = {
      event: "contactUsSubmission",
      item: "Contact us form",
    };
    const submitData: FormValues = {
      type: 2,
      name: `${data.get("firstname")} ${data.get("lastname")}`,
      email: data.get("email") as string,
      jobtitle: data.get("jobtitle") as string,
      organisiation: data.get("company") as string,
      message: data.get("message") as string,
    };

    if (!process.env.NEXT_PUBLIC_CONTACT_FORM_API_ENDPOINT) {
      setShowError(true);
      return (
        <Error statusCode={500} title="Missing Contact Form API Endpoint" />
      );
    }

    try {
      const res = await fetch(
        process.env.NEXT_PUBLIC_CONTACT_FORM_API_ENDPOINT,
        {
          method: "PUT",
          body: JSON.stringify(submitData),
          headers: {
            "content-type": "application/json",
          },
        },
      );
      if (res.ok) {
        setSubmitted(true);
        pushToDataLayer({ ...obj, interaction: "Submitted successfully." });
      } else {
        setShowError(true);
        pushToDataLayer({ ...obj, interaction: "Error in submitting form." });
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      setShowError(true);
      pushToDataLayer({ ...obj, interaction: "Error in submitting form." });
    }
  };

  return (
    <div className="contact rounded-btn border-core-white relative w-[427px] max-w-full border border-solid max-md:grid max-md:h-screen max-md:w-screen">
      {submitted ? (
        <div className="bg-core-white">
          <div className="rounded-t-btn bg-feedback-green px-5 pt-[30px] pb-15">
            <div className="relative !transform-none">
              <Typography.H6 color="core-black" className="normal-case">
                {formSubmittedTitle}
              </Typography.H6>
              <Typography.BODY2 color="core-black pt-4">
                {formSubmittedDescription}
              </Typography.BODY2>
              <div className="contact__close absolute top-0 right-0">
                <Icon
                  className="hover:cursor-pointer"
                  size={40}
                  type={ICON_TYPE.CLOSE}
                  onClick={() => setShowContact(false)}
                />
              </div>
            </div>
          </div>
          <div className="rounded-b-btn bg-core-white">
            <div className="w-full !transform-none px-5 pt-8 pb-40">
              <RichText
                additionalClassName={{
                  paragraph: "!leading-normal",
                  link: "!leading-normal !no-underline",
                }}
                content={json}
              />
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="rounded-t-btn bg-core-black px-5 py-5">
            <div
              className="relative overflow-hidden transition-all duration-500 ease-in-out"
              ref={titleWrapper}
              style={{ height: titleHeight }}
            >
              <div ref={titleAll}>
                <div ref={titleOne}>
                  <Typography.H6
                    {...contentfulProps}
                    color="core-white normal-case"
                  >
                    {stepOneTitle}
                  </Typography.H6>
                  <Typography.BODY2
                    {...contentfulProps}
                    color="core-white pt-3"
                  >
                    {stepOneDescription}
                  </Typography.BODY2>
                </div>
                <div className="flex justify-between gap-16" ref={titleTwo}>
                  <div>
                    <Typography.H6
                      {...contentfulProps}
                      color="core-white normal-case"
                    >
                      {stepTwoTitle}
                    </Typography.H6>
                    <Typography.BODY2
                      {...contentfulProps}
                      color="core-white pt-4"
                    >
                      {handleContact(stepTwoDescription)}
                    </Typography.BODY2>
                  </div>
                  <div className="relative right-0 bottom-0 mt-[67px]">
                    <Image
                      className="max-w-[119px]"
                      width={
                        topic
                          ? topic.image.width
                          : topicsCollection[0].image.width
                      }
                      height={
                        topic
                          ? topic.image.height
                          : topicsCollection[0].image.height
                      }
                      src={`${
                        topic ? topic.image.url : topicsCollection[0].image.url
                      }?fm=webp`}
                      alt="contact"
                      hasPlaceholder
                    />
                  </div>
                </div>
              </div>
              <div className="contact__close absolute top-0 right-0">
                <Icon
                  className="hover:cursor-pointer"
                  size={40}
                  type={ICON_TYPE.CLOSE}
                  onClick={() => closeContactModule()}
                />
              </div>
            </div>
          </div>
          <div
            ref={contactWrapper}
            className="bg-core-white overflow-x-hidden overflow-y-scroll overscroll-none transition-all duration-500 ease-in-out max-md:!h-auto"
            style={{ height: contactHeight }}
          >
            <div
              ref={contactAll}
              className="rounded-b-btn bg-core-white flex flex-nowrap items-start max-md:h-full"
            >
              <div
                className="pt-lg w-full shrink-0 grow px-5 pb-5"
                ref={contactOne}
              >
                <div className="flex items-center gap-3.5">
                  <div className="bg-core-red h-[11px] w-[11px] rounded-4xl" />
                  <Typography.LLABEL1>STEP 1 OF 2</Typography.LLABEL1>
                </div>
                <Typography.BODY1 className="pt-5 !text-2xl normal-case">
                  {intro}
                </Typography.BODY1>
                <div className="flex flex-wrap gap-4 pt-10">
                  {topicsCollection.map(item => (
                    <Button
                      key={item.topic}
                      primary
                      fullHeight={false}
                      className="px-3 text-sm"
                      type={BUTTON_SUBTYPE_CLASS.PRIMARY_BLACK_OUTLINE}
                      content={{ text: item.topic }}
                      onClick={() => {
                        setTopic(item);
                        setStep(2);
                      }}
                    />
                  ))}
                </div>
              </div>
              {step === 2 && (
                <div
                  className="rounded-b-btn bg-core-white w-full shrink-0 grow px-5 py-5"
                  ref={contactTwo}
                >
                  <div className="flex justify-between">
                    <div className="flex items-center gap-3.5">
                      <div className="bg-core-red h-[11px] w-[11px] rounded-4xl" />
                      <Typography.LLABEL1>STEP 2 OF 2</Typography.LLABEL1>
                    </div>
                    <Icon
                      className="hover:cursor-pointer"
                      size={40}
                      type={ICON_TYPE.PREV}
                      stroke="black"
                      onClick={() => {
                        setTopic(undefined);
                        setStep(1);
                      }}
                    />
                  </div>
                  <ContactForm
                    topic={topic}
                    key={step}
                    isLoading={isLoading}
                    error={showError ? error : undefined}
                    handleSubmit={handleSubmit}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
